import { Injectable } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { FilterParser } from '@core/filter-parser';
import { PageFilter } from '@core/model/pagination/filter';
import { PageResult } from '@core/model/pagination/page-result';
import { LocalStorageDataService } from '@core/services/local-storage.service';
import { ObjectUtils, strictMap } from '@core/utils/object-utils';
import { AddTicketScheduleGQL, GetPaginatedTicketsScheduleGQL, GetScheduledTicketsByDateRangeGQL, GetScheduleTicketByBaseTicketGQL, GetScheduleTicketGQL, RecurringTicketsByDateRangeGQL, ScheduleTicketsGQL, SortEnumType, TicketScheduleFilterInput, TicketScheduleMutationType, TicketScheduleSortInput, UpdateTicketScheduleGQL, UpdateTicketScheduleStatsGQL } from '@generated/queries';
import { plainToClass, plainToInstance } from 'class-transformer';
import { map } from 'rxjs/operators';
import { CreateTicketSchedule, TicketSchedule, UpdateTicketSchedule, UpdateTicketScheduleStats } from '../models/ticket-schedule';
import { firstValueFrom, scheduled } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TicketScheduleService {
vmFilters: TicketScheduleFilterInput;

  constructor(
    private localStorageService: LocalStorageDataService,
    private createTicketSchedule: AddTicketScheduleGQL,
    private chnageTicketSchedule: UpdateTicketScheduleGQL,
    private getTicketByTicketId: GetScheduleTicketGQL,
    private getTicketByBaseTicketId: GetScheduleTicketByBaseTicketGQL,
    private getPaginatedSchedule: GetPaginatedTicketsScheduleGQL,
    private getSchedulesByDateRange: GetScheduledTicketsByDateRangeGQL,
    private getRecurringByDateRange: RecurringTicketsByDateRangeGQL,
    private updateScheduleStats: UpdateTicketScheduleStatsGQL
  ) { }

  getTicketScheduleByTicketId(ticketId:string, fetchPolicy?:FetchPolicy
    ): Promise<TicketSchedule> {
      return firstValueFrom(this.getTicketByTicketId
            .fetch(
              {
                ticketId,
              },
              {
                fetchPolicy:
                  fetchPolicy ??
                  this.localStorageService.getFetchPolicy('ticketSchedule'),
              }
            )
            .pipe(
              map((x) => plainToClass(TicketSchedule, x.data.supportTicket.scheduleTickets[0]))
            ));
    }

    getTicketScheduleByBaseTicketId(baseTicketId:string, fetchPolicy?:FetchPolicy
    ): Promise<TicketSchedule> {
      return firstValueFrom(this.getTicketByBaseTicketId
            .fetch(
              {
                baseTicketId,
              },
              {
                fetchPolicy:
                  fetchPolicy ??
                  this.localStorageService.getFetchPolicy('ticketSchedule'),
              }
            )
            .pipe(
              map((x) => plainToClass(TicketSchedule, x.data.supportTicket.scheduleTickets[0]))
            ));
    }

  getPaginatedScheduledTickets(
      filter: PageFilter, policy:FetchPolicy = null,
      siteSearch:string = null,
      problemSearch:string = null,
      facilitySearch:string = null,
      areaSearch:string = null,
      orderBy?:TicketScheduleSortInput,

    ): Promise<PageResult<TicketSchedule>> {
      this.vmFilters = {
        or: [
          {
            ticketsTemplate: {
              ticketNo: {
                contains: filter.search.trim(),
              }
            },

          }
        ],

      };

      const additionalFilters = [];

    if (siteSearch) {
      additionalFilters.push({
        ticketsTemplate: {
          //siteId: { eq: siteSearch.trim() },
          ticketLocationMap:{
            some:{
              locationId:{
                eq:siteSearch.trim()
              }
            }
          }
        }
      });
    }
    if (facilitySearch) {
      additionalFilters.push({
        ticketsTemplate: {
          facilityId: { eq: facilitySearch.trim() },
        }
      });
    }
    if (areaSearch) {
      additionalFilters.push({
        ticketsTemplate: {
          //areaId: { eq: areaSearch.trim() },
          ticketAreaMap:{
            some:{
              areaId:{
                eq:areaSearch.trim()
              }
            }
          }
        }
      });
    }
    if (problemSearch) {
      additionalFilters.push({
        ticketsTemplate: {
          problemId: { eq: problemSearch.trim() },
        }
      });
    }
    if (additionalFilters.length > 0) {
      this.vmFilters = {
        and: [this.vmFilters, ...additionalFilters],
      };
    }

      const filters = FilterParser.parseFilter({
        state: filter.state,
      });
      if (!ObjectUtils.isObjEmpty(filters)) {
        this.vmFilters = {
          and: [filters],
        };
      }
      const paginationFilter = FilterParser.parsePageFilter(filter);
      let order: TicketScheduleSortInput[] = orderBy
        ? [orderBy]
        : [
          {
            createdOn: SortEnumType.Desc,
          }
          ];
      return firstValueFrom(this.getPaginatedSchedule
        .fetch(
          {
            filter: this.vmFilters,
            skip: paginationFilter.skip,
            take: paginationFilter.take,
            order: orderBy
          },
          {
            fetchPolicy: policy?? this.localStorageService.getFetchPolicy('ticketSchedule'),
          }
        )
        .pipe(
          map(
            (x) => new PageResult<TicketSchedule>(x.data.supportTicket.paginatedScheduleTickets as any)
          )
        ));
  }

  getScheduledTicketByTicketId(
    scheduleId: string,
      policy:FetchPolicy = null,

    ): Promise<TicketSchedule> {
      this.vmFilters = {
        and: [
          {
            id: {
                eq: scheduleId
              }

          }
        ],

      };


      return firstValueFrom(this.getPaginatedSchedule
        .fetch(
          {
            filter: this.vmFilters,
            skip: 0,
            take: 1,
          },
          {
            fetchPolicy: policy?? this.localStorageService.getFetchPolicy('ticketSchedule'),
          }
        )
        .pipe(
          map((x) => plainToInstance(TicketSchedule, x.data.supportTicket.paginatedScheduleTickets.items[0]))
        ));
  }

  getTicketScheduleByDateRange(fromDate: Date, toDate: Date, fetchPolicy?: FetchPolicy): Promise<TicketSchedule[]> {
    return firstValueFrom(this.getSchedulesByDateRange
      .fetch(
        {
          startDate: fromDate,
          endDate: toDate,
        },
        {
          fetchPolicy:
            fetchPolicy ??
            this.localStorageService.getFetchPolicy('ticketsSchedule'),
        }
      )
      .pipe(
        map((x) => plainToInstance(TicketSchedule, x.data.supportTicket.scheduleTickets))
      ));
  }

  getRecurringTicketByDateRange(ticketScheduleID: string, fromDate: Date, toDate: Date, fetchPolicy?: FetchPolicy): Promise<TicketSchedule[]> {

    return firstValueFrom(this.getRecurringByDateRange
      .fetch(
        {
          ticketScheduleID: ticketScheduleID,
          fromDate: fromDate,
          toDate: toDate
        },
        {
          fetchPolicy:
            fetchPolicy ??
            this.localStorageService.getFetchPolicy('ticketsSchedule'),
        }
      )
      .pipe(
        map((x) => plainToInstance(TicketSchedule, x.data.supportTicket.recurringTicket))
      ));
  }
  addTicketSchedule(model: TicketSchedule): Promise<string> {
      return firstValueFrom(this.createTicketSchedule
        .mutate({ model: strictMap(CreateTicketSchedule, model) })
        .pipe(map((x) => x.data.supportTicket.ticketSchedule.add)));
    }

  updateTicketSchedule(model: TicketSchedule): Promise<string> {
      
      return firstValueFrom(this.chnageTicketSchedule
        .mutate({ model: strictMap(UpdateTicketSchedule, model) })
        .pipe(map((x) => x.data.supportTicket.ticketSchedule.update)));
    }

    updateTicketScheduleStats(model: UpdateTicketScheduleStats): Promise<string> {
      return firstValueFrom(this.updateScheduleStats
        .mutate({ model: strictMap(UpdateTicketScheduleStats, model) })
        .pipe(map((x) => x.data.supportTicket.ticketSchedule.updateScheduleStats)));
    }


}
